<template>
  <BittsLoading class="team-tables-wrapper" :is-loading="!ready">
    <div>
      <UserManagementDisabledBanner v-if="!userManagementEnabledForOrg" />
      <div class="team-tables-wrapper__header-section">
        <BittsSegment :active-index :tabs @change:segment="onSegmentChange" />
      </div>
      <div class="team-tables-wrapper__controls">
        <BittsQuickFilters
          :key="effectiveView"
          :filter-options="filterOptions"
          :filter-value-options="filterValueOptions"
          data-testid="team-tables-filters"
          @update-filters="handleUpdateFilters"
        />
        <div class="interactivity">
          <BittsInput
            :model-value="search"
            type="search"
            data-testid="team-tables-wrapper-search"
            size="small"
            placeholder="Search for users"
            name="team-search"
            class="min-w-[240px] mt-1"
            @update:model-value="applySearch"
          />
          <div
            v-if="
              isSeatRequestsView &&
              seatRequestSelectedCount &&
              userManagementEnabledForOrg
            "
            class="bulk-actions"
          >
            <component
              :is="isBulkApproveDisabled ? BittsTooltip : 'div'"
              placement="topLeft"
            >
              <BittsButton
                text="Bulk Approve"
                variant="outline"
                type="neutral"
                size="small"
                data-testid="bulk-approve-requests"
                :disabled="isBulkApproveDisabled"
                :left-icon="['fak', 'check']"
                @click="handleBulkApprove"
              />
              <template #title>
                You don't have any available seats. Add seats to approve
                requests
              </template>
            </component>
            <component
              :is="isBulkRejectDisabled ? BittsTooltip : 'div'"
              placement="topLeft"
            >
              <BittsButton
                text="Bulk Reject"
                variant="outline"
                type="danger"
                size="small"
                data-testid="bulk-reject-requests"
                :disabled="isBulkRejectDisabled"
                :left-icon="['fak', 'x']"
                @click="isBulkRejectModalOpen = true"
              />
              <template #title>
                You can only reject up to
                {{ MAX_BULK_REJECT_SEAT_REQUESTS }} requests at a time
              </template>
            </component>
          </div>
          <div
            v-else-if="
              !isSeatRequestsView &&
              selectedCount &&
              userManagementEnabledForOrg
            "
            class="bulk-actions"
          >
            <BittsButton
              text="Edit users"
              variant="outline"
              type="neutral"
              size="small"
              :left-icon="['fak', 'edit']"
              @click="handleEdit"
            />
            <component
              :is="deleteDisabled ? BittsTooltip : 'div'"
              placement="topLeft"
            >
              <BittsButton
                text="Delete users"
                :disabled="deleteDisabled"
                variant="outline"
                type="danger"
                size="small"
                :left-icon="['fak', 'delete']"
                @click="handleDelete"
              />
              <template #title>
                {{ TOOLTIP_SELF }}
              </template>
            </component>
          </div>
          <div
            v-else-if="userManagementEnabledForOrg"
            class="flex items-center gap-x-6"
          >
            <component
              :is="invitesDisabledReason ? UpsellTooltip : 'div'"
              :hide-upsell="
                !canEditUsers || isSubscriptionCancelled || hideUpsellCta
              "
              :skip-modal="true"
              :data-testid="
                invitesDisabledReason
                  ? 'invite-disabled-tooltip'
                  : 'invite-button'
              "
              overlay-class="team-tables-wrapper-disabled-tooltip"
              :button-text="inviteTooltipButtonText"
              :cta-icon="
                inviteTooltipButtonText === 'Add seats'
                  ? ['fak', 'add']
                  : ['fak', 'upgrade']
              "
              placement="bottomLeft"
              :billing-interaction="{
                cta: isFreeTier
                  ? TEAM_CTAS.FREE_NEEDS_MORE_SEATS
                  : TEAM_CTAS.BUY_SEATS,
                event_site: EVENT_SITES.TEAM_TABLE_UPSELL_TOOLTIP,
                talkToSalesReason: mustTalkToSales && 'Need more seats',
              }"
            >
              <template #title>
                <span data-testid="invites-disabled-reason">
                  {{ invitesDisabledReason }}
                </span>
              </template>
              <span>
                <BittsButton
                  text="Invite user"
                  :disabled="!!invitesDisabledReason"
                  type="primary"
                  size="small"
                  data-testid="invite-user"
                  :left-icon="['fak', 'add']"
                  @click="handleInvite"
                />
              </span>
            </component>
          </div>
        </div>
      </div>
      <TeamTable
        v-if="effectiveView === TEAM_MEMBERS"
        ref="teamTableRef"
        :selected-ids="selectedUserIds"
        :is-header-checked="teamHeaderChecked"
        :is-header-indeterminate="teamHeaderIndeterminate"
        :search="debouncedSearch"
        :seat-types="filters.seat_type"
        :user-statuses="filters.user_status"
        :full-access-roles="filters.full_access_role"
        :sales-roles="filters.sales_role"
        :login-methods="filters.login_method"
        @pick="toggleUser"
        @pick-all="onUserHeaderChecked"
      />
      <SeatRequestsTable
        v-else
        ref="seatRequestsTableRef"
        :selected-ids="selectedRequestIds"
        :is-header-checked="seatRequestHeaderChecked"
        :is-header-indeterminate="seatRequestHeaderIndeterminate"
        :search="debouncedSearch"
        :seat-types="filters.seat_type"
        :received-range="filters.received"
        @pick="toggleSeatRequest"
        @pick-all="onSeatRequestHeaderChecked"
      />
      <SeatRequestBulkRejectModal
        v-if="isBulkRejectModalOpen"
        :count="seatRequestSelectedCount"
        :is-rejecting="isRejecting"
        @confirm="handleBulkReject"
        @close="isBulkRejectModalOpen = false"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsInput,
  BittsLoading,
  BittsQuickFilters,
  BittsSegment,
  BittsTooltip,
} from '@crossbeam/bitts';
import {
  FilterProp,
  SelectedRange,
} from '@crossbeam/bitts/src/BittsQuickFilter/types';
import { BittsIcon } from '@crossbeam/bitts/src/types';
import { EVENT_SITES, TEAM_CTAS } from '@crossbeam/itly';

import { pluralize } from 'humanize-plus';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import UpsellTooltip from '@/components/billing/UpsellTooltip.vue';
import SeatRequestBulkRejectModal from '@/components/team/seat-requests/SeatRequestBulkRejectModal.vue';
import SeatRequestsTable from '@/components/team/seat-requests/SeatRequestsTable.vue';
import TeamTable from '@/components/team/TeamTable.vue';
import UserManagementDisabledBanner from '@/components/team/UserManagementDisabledBanner.vue';

import useTableRowSelection from '@/composables/team/useTableRowSelection';
import useTeamInvites from '@/composables/team/useTeamInvites';
import useTeamTable from '@/composables/team/useTeamTable';
import useAuth from '@/composables/useAuth';
import useDebouncedSearch from '@/composables/useDebouncedSearch';
import useSeats from '@/composables/useSeats';
import {
  LOGIN_METHOD_LABELS,
  SALES_EDGE_ROLES,
  TOOLTIP_SELF,
} from '@/constants/team';
import { CORE, INTEGRATION, SALES } from '@/constants/team_v4';
import {
  allReady,
  useBillingStore,
  useFlashesStore,
  useRolesStore,
  useSeatRequestsStore,
  useTeamStore,
} from '@/stores';
import {
  MAX_BULK_REJECT_SEAT_REQUESTS,
  SeatRequest,
} from '@/types/seat_requests';

const router = useRouter();
const route = useRoute();

const billingStore = useBillingStore();
const teamStore = useTeamStore();
const rolesStore = useRolesStore();
const seatRequestsStore = useSeatRequestsStore();
const flashesStore = useFlashesStore();

const { isFreeTier, isSubscriptionCancelled } = storeToRefs(billingStore);
const { fullAccessRequests, salesAccessRequests } =
  storeToRefs(seatRequestsStore);

const ready = allReady(teamStore, rolesStore, billingStore, seatRequestsStore);

const TEAM_MEMBERS = 'team-members';
const SEAT_REQUESTS = 'seat-requests';
const seatRequestCount = computed(() => {
  return fullAccessRequests.value.length + salesAccessRequests.value.length;
});
const tabs = computed(() => {
  return [
    {
      title: 'Team Members',
      id: TEAM_MEMBERS,
      disabled: false,
    },
    {
      title: 'Seat Requests',
      id: SEAT_REQUESTS,
      disabled: !seatRequestCount.value,
      count: seatRequestCount.value || undefined,
      tooltipProps: !seatRequestCount.value
        ? {
            tooltipTitle: 'There are no current seat requests',
            placement: 'bottomLeft',
          }
        : undefined,
    },
  ];
});
const SEAT_TYPE_FILTER: FilterProp = {
  label: 'Seat Type',
  value: 'seat_type',
  faIcon: ['fak', 'seat'] as BittsIcon,
  datatype: 'radio',
  useSearch: false,
};
const RECEIVED_FILTER: FilterProp = {
  label: 'Received',
  value: 'received',
  faIcon: ['fak', 'calendar'] as BittsIcon,
  datatype: 'datetime',
};
const SEAT_REQUEST_FILTERS: FilterProp[] = [SEAT_TYPE_FILTER, RECEIVED_FILTER];
const STATUS_FILTER: FilterProp = {
  label: 'Status',
  value: 'user_status',
  faIcon: ['fak', 'status'] as BittsIcon,
  datatype: 'radio',
  useSearch: false,
};
const TEAM_MEMBER_FILTERS: FilterProp[] = [
  SEAT_TYPE_FILTER,
  STATUS_FILTER,
  {
    label: 'Full Access Role',
    value: 'full_access_role',
    faIcon: ['fak', 'person'] as BittsIcon,
  },
  {
    label: 'Sales Role',
    value: 'sales_role',
    faIcon: ['fak', 'sales'] as BittsIcon,
  },
  {
    label: 'Login Method',
    value: 'login_method',
    faIcon: ['fak', 'lock'] as BittsIcon,
  },
];

const SEAT_TYPE_VALUE_OPTIONS: FilterProp[] = [
  { label: 'All seat types', value: 'all' },
  { label: 'Full Access', value: CORE },
  { label: 'Sales', value: SALES },
  { label: 'Integration', value: INTEGRATION },
];
const STATUS_VALUE_OPTIONS: FilterProp[] = [
  { label: 'All statuses', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Invited', value: 'invited' },
];
const SALES_ROLE_VALUE_OPTIONS: FilterProp[] = SALES_EDGE_ROLES.map((role) => ({
  label: role.name,
  value: role.id,
}));
const LOGIN_METHOD_VALUE_OPTIONS: FilterProp[] = Object.entries(
  LOGIN_METHOD_LABELS,
).map(([value, label]) => ({ label, value }));
const fullAccessRoleOptions = computed<FilterProp[]>(() =>
  rolesStore.allRoles
    .filter(
      (role) => role.seat_type !== SALES && role.seat_type !== INTEGRATION,
    )
    .map((role) => ({ label: role.name, value: role.id })),
);
const filterValueOptions = computed<Record<string, FilterProp[]>>(() => ({
  seat_type: SEAT_TYPE_VALUE_OPTIONS,
  user_status: STATUS_VALUE_OPTIONS,
  full_access_role: fullAccessRoleOptions.value,
  sales_role: SALES_ROLE_VALUE_OPTIONS,
  login_method: LOGIN_METHOD_VALUE_OPTIONS,
}));

const ARRAY_FILTER_KEYS = [
  'seat_type',
  'user_status',
  'full_access_role',
  'sales_role',
  'login_method',
] as const;

const filters = reactive<{
  seat_type: string[];
  user_status: string[];
  full_access_role: string[];
  sales_role: string[];
  login_method: string[];
  received: [string, string] | [];
}>({
  seat_type: [],
  user_status: [],
  full_access_role: [],
  sales_role: [],
  login_method: [],
  received: [],
});

function toFilterArray(
  value: (string | number)[] | Record<string, unknown> | null | undefined,
): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}
function toDateRange(range: SelectedRange | undefined): [string, string] | [] {
  return Array.isArray(range) && range.length === 2
    ? [String(range[0]), String(range[1])]
    : [];
}
function handleUpdateFilters(filterInfo: {
  filterValues: Record<
    string,
    (string | number)[] | Record<string, unknown> | null
  >;
  filterRanges: Record<string, SelectedRange>;
}) {
  const { filterValues, filterRanges } = filterInfo;
  ARRAY_FILTER_KEYS.forEach((key) => {
    filters[key] = toFilterArray(filterValues[key]);
  });
  filters.received = toDateRange(filterRanges.received);
}
function resetFilters() {
  ARRAY_FILTER_KEYS.forEach((key) => {
    filters[key] = [];
  });
  filters.received = [];
}

const currentView = ref(TEAM_MEMBERS);
const effectiveView = computed(() =>
  currentView.value === SEAT_REQUESTS && !seatRequestCount.value
    ? TEAM_MEMBERS
    : currentView.value,
);
const activeIndex = computed(() =>
  tabs.value.findIndex((tab) => tab.id === effectiveView.value),
);
const filterOptions = computed<FilterProp[]>(() =>
  effectiveView.value === SEAT_REQUESTS
    ? SEAT_REQUEST_FILTERS
    : TEAM_MEMBER_FILTERS,
);
const isSeatRequestsView = computed(
  () => effectiveView.value === SEAT_REQUESTS,
);

const teamTableRef = ref<InstanceType<typeof TeamTable>>();
const seatRequestsTableRef = ref<InstanceType<typeof SeatRequestsTable>>();
const selectableUserIds = computed<number[]>(
  () => teamTableRef.value?.selectableIds ?? [],
);
const selectableRequestIds = computed<string[]>(
  () => seatRequestsTableRef.value?.selectableIds ?? [],
);

const {
  selectedIds: selectedRequestIds,
  selectedVisibleIds: visibleSelectedRequestIds,
  selectedVisibleCount: seatRequestSelectedCount,
  headerChecked: seatRequestHeaderChecked,
  headerIndeterminate: seatRequestHeaderIndeterminate,
  toggle: toggleSeatRequest,
  onHeaderChecked: onSeatRequestHeaderChecked,
  clear: clearSeatRequestSelection,
} = useTableRowSelection<string>(selectableRequestIds);

const isBulkRejectModalOpen = ref(false);
const isRejecting = ref(false);

const { atSeatLimit, resolveSeatType } = useSeats();
const isBulkApproveDisabled = computed(() => atSeatLimit.value);
const isBulkRejectDisabled = computed(
  () => seatRequestSelectedCount.value > MAX_BULK_REJECT_SEAT_REQUESTS,
);

async function handleBulkApprove() {
  const requests = visibleSelectedRequestIds.value
    .map((id) => seatRequestsStore.getSeatRequestByUuid(id))
    .filter((request): request is SeatRequest => !!request);
  const staleCount = [...selectedRequestIds.value].filter(
    (id) => !seatRequestsStore.getSeatRequestByUuid(id),
  ).length;
  if (staleCount) {
    flashesStore.addInfoFlash({
      message: `${staleCount} seat ${pluralize(staleCount, 'request')} no longer pending`,
    });
  }
  const firstRequest = requests[0];
  if (!firstRequest) {
    clearSeatRequestSelection();
    return;
  }
  await router.push({
    name: 'approve_team_seat_request',
    query: {
      cancelDestination: route.name?.toString(),
      seat_type: resolveSeatType(firstRequest.type),
      emails: requests.map((request) => request.email).join(','),
    },
  });
  clearSeatRequestSelection();
}

async function handleBulkReject() {
  const ids = visibleSelectedRequestIds.value;
  if (!ids.length) {
    isBulkRejectModalOpen.value = false;
    return;
  }
  isRejecting.value = true;
  const rejected = await seatRequestsStore.bulkRejectSeatRequests(ids);
  if (rejected) {
    isBulkRejectModalOpen.value = false;
  }
  isRejecting.value = false;
}

function onSegmentChange(segment: string) {
  currentView.value = segment;
}

const {
  canEditUsers,
  deleteDisabled,
  handleDelete,
  handleEdit,
  selectedCount,
  selectedUserIds,
  headerChecked: teamHeaderChecked,
  headerIndeterminate: teamHeaderIndeterminate,
  toggle: toggleUser,
  onHeaderChecked: onUserHeaderChecked,
  clear: clearUserSelection,
} = useTeamTable(selectableUserIds);

const {
  invitesDisabledReason,
  inviteTooltipButtonText,
  mustTalkToSales,
  handleInvite,
  hideUpsellCta,
} = useTeamInvites();

const { userManagementEnabledForOrg } = useAuth();

const {
  searchValue: search,
  delayedSearchValue: debouncedSearch,
  search: applySearch,
} = useDebouncedSearch({ limit: 200 });

watch(effectiveView, () => {
  applySearch('');
  resetFilters();
  clearUserSelection();
  clearSeatRequestSelection();
  isBulkRejectModalOpen.value = false;
});
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.team-tables-wrapper__controls {
  @apply flex justify-between items-start gap-8 mb-16;
}
.team-tables-wrapper__header-section {
  @apply flex justify-between items-center mb-16;
}

.team-tables-wrapper {
  @apply overflow-hidden mt-40;

  .bulk-actions,
  .interactivity {
    @apply flex justify-between items-end gap-8 ml-8;
  }
}
</style>
